import ColumnBase from "./ColumnBase.jsx";
import {WIDGET_KEYS} from "../../other/constants/WidgetKeys.jsx";
import {ColumnStore} from "@bryntum/schedulerpro";
import { CHECKLIST_STATE, ICON_COL_DEF_WIDTH } from "../../other/constants/OtherKeys.jsx";
import { defaultGroupRenderer, getNameInitials } from "../../other/utils/utils.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import { HasIconColumnTooltipMixin } from "./mixins/HasIconColumnTooltipMixin.jsx";

const getChecklistIcon = (checklistStatus = CHECKLIST_STATE.NO_CHECKLIST) => {
  let checklistColor = "";
  if (checklistStatus === CHECKLIST_STATE.IN_COMPLETE) checklistColor = "text-[#dc7f00]";
  if (checklistStatus === CHECKLIST_STATE.ALL_CHECKED) checklistColor = "text-[#0a88e9]";
  return `<i class="swivu-checklist-icon-tooltip fa-regular fa-list-check ${checklistColor}"></i>`;
}

export default class ChecklistColumn extends HasIconColumnTooltipMixin(ColumnBase) {
  static get $name() {
    return "ChecklistColumn";
  }

  static get type() {
    return WIDGET_KEYS.checklistColumn;
  }

  static get defaults() {
    return {
      text: "Checklist",
      width: 30,
      minWidth: ICON_COL_DEF_WIDTH,
      editor: false,
      searchable: false,
      align: "center",
      field: "checklistStatus",
      cellCls: "cursor-pointer",
      cls: "swivu-icon-column",
      sealed: true,
      filterable: false,
      htmlEncode: false,
      renderer({ value }) {
        return getChecklistIcon(value);
      },
      groupRenderer({ groupRowFor, ...rest }) {
        return defaultGroupRenderer({ groupRowFor: getChecklistIcon(groupRowFor), ...rest });
      },
      headerRenderer() {
        return getChecklistIcon();
      }
    }
  }

  /**
   * Get the CSS selector for the tooltip
   * @returns {String} - The CSS selector
   */
  getTooltipSelector() {
    return ".swivu-checklist-icon-tooltip";
  }

  /**
   * Get the checklists to display in the tooltip
   * @param {Object} record - The record to get checklists for
   * @returns {Array} - The checklists to display
   */
  getTooltipItems(record) {
    return this.project.checklistStore.query(checklist => {
      if (record.isTask) return checklist.taskId === record.id;
      if (record.isResource) return checklist.resourceId === record.id;
      if (record.isClient) return checklist.clientId === record.id;
      return false;
    });
  }

  /**
   * Format the checklists for display in the tooltip
   * @param {Array} checklists - The checklists to format
   * @returns {Array} - The formatted checklists
   */
  formatTooltipItems(checklists) {
    return checklists.map((checklistRecord, index) => {
      return {
        tag: "div",
        dataset: {
          checklistId: checklistRecord.id
        },
        class: `tooltip-contact-container flex flex-col gap-2 ${index > 0 ? 'pt-3 mt-3 border-t border-border' : ''}`,
        children: [
          {
            tag: "div",
            class: "contact-pill contact-header font-medium text-sm cursor-pointer hover:bg-accent/80 transition-colors rounded-md px-3 py-2",
            html: `<div class="contact-header">${checklistRecord.title || "Untitled Checklist"}</div>`
          },
          {
            tag: "div",
            class: "px-3",
            html: this.renderAssignedUsers(checklistRecord)
          }
        ]
      };
    });
  }

  /**
   * Handle clicks on checklists in the tooltip
   * @param {Event} event - The click event
   * @param {Object} record - The record the tooltip is for
   */
  handleTooltipClick(event, record) {
    if (this.onChecklistTimeout) clearTimeout(this.onChecklistTimeout);

    this.onChecklistTimeout = setTimeout(() => {
      const tooltipContainer = event.target.closest(".tooltip-contact-container");
      if (!tooltipContainer) return;

      const checklistId = tooltipContainer.dataset.checklistId;
      const checklistRecord = this.project.checklistStore.getById(checklistId);
      if (!checklistRecord) return;

      // Check if clicked on user avatar
      const avatarElement = event.target.closest(".swivu-tooltip-pill-item-img");
      if (avatarElement && avatarElement.dataset.id) {
        const userRecord = this.project.boardUsersStore.getById(avatarElement.dataset.id);
        if (userRecord) {
          this.otherParams.editorHandler.openEditor(EDITOR_KEYS.userEditor, userRecord, {
            user: userRecord,
          });
        }
        return;
      }

      // Check if clicked on checklist header
      const headerElement = event.target.closest(".contact-header");
      if (headerElement) {
        this.openChecklistEditor(checklistRecord, record);
      }
    }, 100);
  }

  openChecklistEditor(checklistRecord) {
    const entity = checklistRecord.entity;
    this.otherParams.editorHandler.openEditor(EDITOR_KEYS.checklistEditor, null, {
      entity,
      checklistId: checklistRecord.id
    }, { by: "recordType", useExisting: true });
  }

  // onColumnDestroy is now handled by the mixin

  renderAssignedUsers(checklistRecord) {
    if (!checklistRecord.assignedBoardUsers || checklistRecord.assignedBoardUsers.length === 0) {
      return "<span class=\"text-xs text-muted-foreground\">No assigned users</span>";
    }

    // Get the board users from the assignedBoardUsers array
    const assignedUsers = checklistRecord.assignedBoardUsers.map(userId => {
      return this.project.boardUsersStore.getById(userId);
    }).filter(Boolean); // Filter out any undefined users

    if (assignedUsers.length === 0) {
      return "<span class=\"text-xs text-muted-foreground\">No assigned users</span>";
    }

    let html = '<div class="flex items-center gap-1.5">';

    // Add avatars for displayed users
    assignedUsers.forEach(user => {
      if (user.profileImageURL) {
        html += `<img src="${user.profileImageURL}" alt="${user.name}" title="${user.name}" class="w-7 h-7 rounded-full border border-border hover:ring-2 hover:ring-primary/50 transition-all cursor-pointer swivu-tooltip-pill-item-img" data-id="${user.id}" />`;
      } else {
        // Create initials avatar if no profile image
        html += `<div class='swivu-avatar-initials swivu-tooltip-pill-item-img w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium bg-primary/10 text-primary border border-border hover:ring-2 hover:ring-primary/50 transition-all cursor-pointer' data-id="${user.id}">
          ${getNameInitials(user.name)}
        </div>`
      }
    });

    html += '</div>';
    return html;
  }
}

ColumnStore.registerColumnType(ChecklistColumn);
